import api from './api';

export interface LegalDocument {
  id: string;
  title: string;
  description?: string;
  audience: 'User' | 'Business';
  documentType: 'TermsOfUse' | 'PrivacyPolicy' | 'CookiePolicy' | 'PartnershipTerms';
  version: number;
  fileUrl: string;
  isActive: boolean;
  createdAt: string;
  publishedAt?: string;
}

export interface UserLegalStatus {
  audience: string;
  documentType: string;
  hasAcceptedLatest: boolean;
  lastAcceptedAt?: string;
  acceptedVersion: number;
  latestVersion: number;
  updateRequired: boolean;
}

export interface LegalAcceptanceRequest {
  legalDocumentId: string;
}

class LegalService {
  // Buscar documentos legais ativos para um público específico
  async getActiveDocuments(audience: string): Promise<LegalDocument[]> {
    try {
      // Usar o novo endpoint do AdminUsersController
      const response = await api.get(`/admin/users/terms/documents?audience=${audience}`);
      return response.data?.documents || [];
    } catch (error) {
      console.error('Erro ao buscar documentos legais:', error);
      return [];
    }
  }

  // Buscar documento específico por tipo e público
  async getDocumentByType(audience: string, documentType: string): Promise<LegalDocument | null> {
    try {
      const response = await api.get(`/legal/latest-by-type?audience=${audience}&documentType=${documentType}`);
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar documento por tipo:', error);
      return null;
    }
  }

  // Verificar status legal do usuário
  async getUserLegalStatus(audience: string): Promise<UserLegalStatus | null> {
    try {
      // Usar o novo endpoint do AdminUsersController
      const response = await api.get(`/admin/users/terms/check`);
      const userStatus = response.data?.userStatus?.find((status: any) => status.audience === audience);
      return userStatus || null;
    } catch (error) {
      console.error('Erro ao buscar status legal do usuário:', error);
      return null;
    }
  }

  // Aceitar termos legais
  async acceptTerms(legalDocumentId: string): Promise<boolean> {
    try {
      // Usar o novo endpoint do AdminUsersController
      const response = await api.post('/admin/users/terms/accept', {
        legalDocumentId
      });
      return response.status === 200;
    } catch (error) {
      console.error('Erro ao aceitar termos:', error);
      return false;
    }
  }

  // Verificar se o usuário precisa aceitar termos
  async checkTermsAcceptanceRequired(): Promise<{
    requiresAcceptance: boolean;
    documents: LegalDocument[];
    userStatus: UserLegalStatus[];
  }> {
    try {
      console.log('LegalService: Iniciando verificação de aceite de termos...');
      
      // Verificar cache local primeiro
      const cacheKey = 'legal_acceptance_cache';
      const cachedData = localStorage.getItem(cacheKey);
      
      if (cachedData) {
        try {
          const parsed = JSON.parse(cachedData);
          const now = Date.now();
          const cacheAge = now - parsed.timestamp;
          const cacheExpiry = 5 * 60 * 1000; // 5 minutos
          
          if (cacheAge < cacheExpiry && !parsed.requiresAcceptance) {
            console.log('LegalService: Usando cache local - usuário já aceitou os termos');
            return {
              requiresAcceptance: false,
              documents: [],
              userStatus: []
            };
          }
        } catch (error) {
          console.error('LegalService: Erro ao parsear cache:', error);
        }
      }
      
      // Detectar se é Business User baseado no localStorage
      const savedUser = localStorage.getItem('admin_user');
      let isBusinessUser = false;
      
      if (savedUser) {
        try {
          const userData = JSON.parse(savedUser);
          isBusinessUser = userData.userType === 'business_admin';
          console.log('LegalService: Tipo de usuário detectado:', userData.userType);
        } catch (error) {
          console.error('LegalService: Erro ao parsear dados do usuário:', error);
        }
      }
      
      if (isBusinessUser) {
        // Para Business Users: apenas requests de Business
        console.log('LegalService: Business User detectado, fazendo apenas requests de Business...');
        
        const businessDocuments = await this.getActiveDocuments('Business');
        console.log('LegalService: Documentos de negócios:', businessDocuments);
        
        const businessStatus = await this.getUserLegalStatus('Business');
        console.log('LegalService: Status do usuário para negócios:', businessStatus);
        
        const allDocuments = businessDocuments;
        const allStatus = businessStatus ? [businessStatus] : [];
        
        console.log('LegalService: Todos os documentos para Business:', allDocuments);
        console.log('LegalService: Todos os status para Business:', allStatus);
        
        const requiresAcceptance = allStatus.some(status => !status.hasAcceptedLatest);
        console.log('LegalService: requiresAcceptance =', requiresAcceptance);
        
        // Salvar no cache se não precisar de aceite
        if (!requiresAcceptance) {
          this.saveAcceptanceCache();
        }
        
        return {
          requiresAcceptance,
          documents: allDocuments,
          userStatus: allStatus
        };
      } else {
        // Para outros usuários: requests de User
        console.log('LegalService: Usuário não-Business detectado, fazendo apenas requests de User...');
        
        const userDocuments = await this.getActiveDocuments('User');
        console.log('LegalService: Documentos de usuários:', userDocuments);
        
        const userStatus = await this.getUserLegalStatus('User');
        console.log('LegalService: Status do usuário para usuários:', userStatus);
        
        const allDocuments = userDocuments;
        const allStatus = userStatus ? [userStatus] : [];
        
        console.log('LegalService: Todos os documentos para User:', allDocuments);
        console.log('LegalService: Todos os status para User:', allStatus);
        
        const requiresAcceptance = allStatus.some(status => !status.hasAcceptedLatest);
        console.log('LegalService: requiresAcceptance =', requiresAcceptance);
        
        // Salvar no cache se não precisar de aceite
        if (!requiresAcceptance) {
          this.saveAcceptanceCache();
        }
        
        return {
          requiresAcceptance,
          documents: allDocuments,
          userStatus: allStatus
        };
      }
    } catch (error) {
      console.error('LegalService: Erro ao verificar aceite de termos:', error);
      return {
        requiresAcceptance: false,
        documents: [],
        userStatus: []
      };
    }
  }

  // Salvar cache de aceite
  private saveAcceptanceCache(): void {
    try {
      const cacheData = {
        timestamp: Date.now(),
        requiresAcceptance: false
      };
      localStorage.setItem('legal_acceptance_cache', JSON.stringify(cacheData));
      console.log('LegalService: Cache de aceite salvo');
    } catch (error) {
      console.error('LegalService: Erro ao salvar cache:', error);
    }
  }

  // Limpar cache de aceite (chamado quando aceita novos termos)
  clearAcceptanceCache(): void {
    try {
      localStorage.removeItem('legal_acceptance_cache');
      console.log('LegalService: Cache de aceite limpo');
    } catch (error) {
      console.error('LegalService: Erro ao limpar cache:', error);
    }
  }
}

export const legalService = new LegalService();
export default legalService;
